import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Link } from 'arcway/lib/client'

import { Markdown, MarkdownOutline } from '#components/markdown.jsx'

function GuideNavigation({ articles, currentSlug }) {
  return (
    <nav aria-label='Guides'>
      <Link aria-current={!currentSlug ? 'page' : undefined} href='/docs'>
        Overview
      </Link>
      <p className='guide-eyebrow'>Guides</p>
      <ol>
        {articles.map(item => (
          <li key={item.slug}>
            <Link
              aria-current={item.slug === currentSlug ? 'page' : undefined}
              href={`/docs/${item.slug}`}
            >
              {item.title}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  )
}

function DocumentationShell({ articles, currentSlug, children }) {
  return (
    <div className='guide-wrap guide-reading'>
      <aside className='guide-sidebar'>
        <div className='guide-desktop-nav'>
          <GuideNavigation articles={articles} currentSlug={currentSlug} />
        </div>
        <details className='guide-mobile-nav' key={currentSlug || 'overview'}>
          <summary>Browse documentation</summary>
          <GuideNavigation articles={articles} currentSlug={currentSlug} />
        </details>
      </aside>
      {children}
    </div>
  )
}

export function DocumentationIndex({ articles }) {
  return (
    <DocumentationShell articles={articles}>
      <article className='guide-article guide-overview'>
        <header>
          <p className='guide-eyebrow'>Mono documentation</p>
          <h1>Documentation</h1>
          <p className='guide-intro'>
            Learn how to create your show, publish episodes, and share your
            podcast with listeners.
          </p>
        </header>
        <section className='guide-overview-start'>
          <h2>Start with your first show</h2>
          <p>
            Create your account, set up your podcast, and get ready to publish.
            The getting started guide walks you through each step.
          </p>
          <Link href='/docs/getting-started'>
            Get started <ArrowRight aria-hidden='true' />
          </Link>
        </section>
        <section>
          <h2>Explore the guides</h2>
          <ul className='guide-overview-list'>
            {articles.map(article => (
              <li key={article.slug}>
                <Link href={`/docs/${article.slug}`}>{article.title}</Link>
                <p>{article.description}</p>
              </li>
            ))}
          </ul>
        </section>
      </article>
    </DocumentationShell>
  )
}

export function DocumentationArticle({ article, articles = [] }) {
  const position = articles.findIndex(item => item.slug === article.slug)
  const previous = articles[position - 1]
  const next = position >= 0 ? articles[position + 1] : null
  return (
    <DocumentationShell articles={articles} currentSlug={article.slug}>
      <article className='guide-article'>
        <header>
          <p className='guide-eyebrow'>Mono guide</p>
          <h1>{article.title}</h1>
          <p className='guide-intro'>{article.description}</p>
        </header>
        <nav className='guide-outline' aria-label='On this page'>
          <h2>On this page</h2>
          <MarkdownOutline>{article.markdown}</MarkdownOutline>
        </nav>
        <div className='guide-prose'>
          <Markdown headingAnchors>{article.markdown}</Markdown>
        </div>
        <nav className='guide-pagination' aria-label='Previous and next guides'>
          {previous && (
            <Link href={`/docs/${previous.slug}`}>
              <span>Previous guide</span>
              <strong>
                <ArrowLeft aria-hidden='true' />
                {previous.title}
              </strong>
            </Link>
          )}
          {next && (
            <Link className='guide-next' href={`/docs/${next.slug}`}>
              <span>Next guide</span>
              <strong>
                {next.title}
                <ArrowRight aria-hidden='true' />
              </strong>
            </Link>
          )}
        </nav>
      </article>
    </DocumentationShell>
  )
}
